<script setup>
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
import ModalUserPermissions from "@/Components/ModalUserPermissions.vue";
import Swal from "sweetalert2";
import { useForm, usePage, router } from "@inertiajs/vue3";
import { defineProps, ref, computed, watch, onMounted } from "vue";

const props = defineProps({
  users: {
    type: Object,
    required: true,
  },
  roles: {
    type: Object,
    required: true,
  },
  prodis: {
    type: Object,
    required: true,
  },
  permissions: {
    type: Object,
    required: false,
  },
});

const form = useForm({
  id: null,
  name: "",
  email: "",
  username: "",
  password: "",
  role: "",
  kodeprodi: "",
});

// Permissions state
const isModalPermissions = ref(false);
const currentUser = ref(null);
const userPermissions = ref([]);
const allPermissions = ref([]);
const rolePermissions = ref([]);
const loading = ref(false);
const saving = ref(false);

onMounted(() => {
  if (props.users.data.length > 0) {
    const user = props.users.data[0];
    form.id = user.id;
    form.name = user.name;
    form.email = user.email;
    form.username = user.username;
    form.role = user.role;
    form.kodeprodi = user.kodeprodi;

    // Load permissions after user data is set
    loadUserPermissions();
  }
});

watch(
  () => props.users.data,
  (newData) => {
    if (newData.length > 0) {
      const user = newData[0];

      form.id = user.id;
      form.name = user.name;
      form.email = user.email;
      form.username = user.username;
      form.role = user.role;
      form.kodeprodi = user.kodeprodi;
    }
  },
  { immediate: true, deep: true }
);

const submit = () => {
  form.put(route("users.update", { id: form.id }), {
    data: {
      id: form.id,
      name: form.name,
      email: form.email,
      username: form.username,
      role: form.role,
      kodeprodi: form.kodeprodi,
    },
    onSuccess: () => {
      Swal.fire({
        title: "Berhasil!",
        text: "Data berhasil diperbarui.",
        icon: "success",
        confirmButtonText: "OK",
      });
      // Reload permissions after successful update
      loadUserPermissions();
    },
    onError: (errors) => {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat mengirim data.",
        icon: "error",
        confirmButtonText: "Coba Lagi",
      });
    },
  });
};

const changePassword = () => {
  if (!form.password) {
    Swal.fire({
      title: "Password Required!",
      text: "Please enter a new password.",
      icon: "warning",
      confirmButtonText: "OK",
    });
    return;
  }

  form.put(route("users.updatesandi", { id: form.id }), {
    data: {
      id: form.id,
      password: form.password,
    },
    onSuccess: () => {
      form.password = ""; // Clear password field
      Swal.fire({
        title: "Berhasil!",
        text: "Password berhasil diperbarui.",
        icon: "success",
        confirmButtonText: "OK",
      });
    },
    onError: (errors) => {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat mengirim data.",
        icon: "error",
        confirmButtonText: "Coba Lagi",
      });
    },
  });
};

// Permissions methods
const loadUserPermissions = async () => {
  if (!form.id) return;

  loading.value = true;
  try {
    const response = await fetch(route("users.permissions.get", form.id));
    const data = await response.json();

    allPermissions.value = data.all_permissions;
    userPermissions.value = [...data.user.direct_permissions];
    rolePermissions.value = [...data.user.role_permissions];
    currentUser.value = data.user;
  } catch (error) {
    console.error("Error loading permissions:", error);
    Swal.fire({
      title: "Error!",
      text: "Failed to load user permissions.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    loading.value = false;
  }
};

const updateUserPermissions = async () => {
  if (!form.id) return;

  saving.value = true;
  try {
    const response = await fetch(route("users.permissions.update", form.id), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        permissions: userPermissions.value,
      }),
    });

    if (response.ok) {
      Swal.fire({
        title: "Success!",
        text: "User permissions updated successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
      await loadUserPermissions(); // Reload permissions
    } else {
      throw new Error("Failed to update permissions");
    }
  } catch (error) {
    console.error("Error updating permissions:", error);
    Swal.fire({
      title: "Error!",
      text: "Failed to update user permissions.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    saving.value = false;
  }
};

const isRolePermission = (permissionId) => {
  return rolePermissions.value.includes(permissionId);
};

const openPermissionsModal = () => {
  currentUser.value = {
    id: form.id,
    name: form.name,
  };
  isModalPermissions.value = true;
};

const handlePermissionsUpdated = () => {
  // Reload permissions data when modal updates permissions
  loadUserPermissions();
};
</script>

<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="container px-2 mx-auto sm:px-4">
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Users', href: route('users.index') },
            { name: 'Edit User', href: route('users.show', form.id) },
          ]"
        />
        <div
          class="grid grid-cols-1 gap-4 px-2 py-4 sm:gap-6 sm:px-4 lg:grid-cols-3"
        >
          <!-- Left Column - User Form -->
          <div class="lg:col-span-2">
            <div class="p-4 bg-white border shadow-sm sm:p-6 rounded-xl">
              <h1
                class="mb-4 text-lg font-semibold text-gray-900 sm:mb-6 sm:text-xl"
              >
                Edit User
              </h1>
              <form class="space-y-4 sm:space-y-6" @submit.prevent="submit">
                <!-- Input Fields -->
                <InputField label="Nama" icon="pen" v-model="form.name" />
                <InputError :message="form.errors.name" class="mt-2" />

                <InputField
                  label="Email"
                  type="email"
                  icon="at"
                  v-model="form.email"
                />
                <InputError :message="form.errors.email" class="mt-2" />

                <InputField
                  label="Username"
                  icon="user"
                  v-model="form.username"
                />
                <InputError :message="form.errors.username" class="mt-2" />

                <!-- Roles Dropdown -->
                <div>
                  <InputLabel for="Roles" value="Roles" class="mb-2" />
                  <select
                    class="w-full pr-3 border-0 rounded-md pl-9 text-grey-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    v-model="form.role"
                    id="role"
                  >
                    <option value="">Pilih Roles</option>
                    <option
                      v-for="role in roles.data"
                      :key="role.id"
                      :value="role.id"
                    >
                      {{ role.title }}
                    </option>
                  </select>
                  <InputError :message="form.errors.role" class="mt-2" />
                </div>

                <!-- Program Studi Dropdown -->
                <div>
                  <InputLabel
                    for="Program Studi"
                    value="Program Studi"
                    class="mb-2"
                  />
                  <select
                    class="w-full pr-3 border-0 rounded-md pl-9 text-grey-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    v-model="form.kodeprodi"
                  >
                    <option value="">Pilih Program Studi</option>
                    <option
                      v-for="prodi in prodis.data"
                      :key="prodi.id"
                      :value="prodi.id"
                    >
                      {{ prodi.jenjang }} - {{ prodi.name }}
                    </option>
                  </select>
                  <InputError :message="form.errors.kodeprodi" class="mt-2" />
                </div>

                <!-- Password Section -->
                <div class="pt-4 border-t sm:pt-6">
                  <h3
                    class="mb-3 text-base font-medium text-gray-900 sm:mb-4 sm:text-lg"
                  >
                    Change Password
                  </h3>
                  <InputField
                    label="New Password"
                    type="password"
                    icon="key"
                    v-model="form.password"
                    placeholder="Leave blank to keep current password"
                  />
                  <InputError :message="form.errors.password" class="mt-2" />
                </div>

                <!-- Submit Buttons -->
                <div
                  class="flex flex-col gap-3 pt-4 border-t sm:pt-6 sm:flex-row"
                >
                  <PrimaryBtn :disabled="form.processing" class="sm:flex-1">
                    <i class="mr-2 fa-solid fa-floppy-disk"></i>
                    <span class="hidden sm:inline">Update User</span>
                    <span class="sm:hidden">Update</span>
                  </PrimaryBtn>
                  <button
                    type="button"
                    @click="changePassword"
                    :disabled="form.processing || !form.password"
                    class="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-green-600 border border-transparent rounded-md hover:bg-green-700 active:bg-green-900 focus:outline-none focus:border-green-900 focus:ring ring-green-300 disabled:opacity-25 sm:flex-1"
                  >
                    <i class="mr-2 fa-solid fa-key"></i>
                    <span class="hidden sm:inline">Update Password</span>
                    <span class="sm:hidden">Password</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Right Column - User Permissions -->
          <div class="lg:col-span-1">
            <div class="p-4 bg-white border shadow-sm sm:p-6 rounded-xl h-fit">
              <div
                class="flex flex-col items-start justify-between mb-4 sm:mb-6"
              >
                <h2
                  class="mb-3 text-lg font-semibold text-gray-900 sm:text-xl sm:mb-4"
                >
                  User Permissions
                </h2>
                <button
                  @click="openPermissionsModal"
                  class="inline-flex items-center justify-center w-full px-3 py-2 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 active:bg-indigo-900 focus:outline-none focus:border-indigo-900 focus:ring ring-indigo-300 disabled:opacity-25 sm:px-4"
                >
                  <i class="mr-2 fa-solid fa-key"></i>
                  <span class="hidden sm:inline">Manage Permissions</span>
                  <span class="sm:hidden">Manage</span>
                </button>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="flex justify-center py-8">
                <div
                  class="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin"
                ></div>
              </div>

              <!-- Permissions Display -->
              <div v-else class="space-y-4 sm:space-y-6">
                <!-- Direct Permissions -->
                <div v-if="userPermissions.length > 0">
                  <h3
                    class="flex items-center mb-2 text-sm font-medium text-gray-900 sm:mb-3"
                  >
                    <i class="mr-2 text-green-600 fa-solid fa-user-check"></i>
                    Direct Permissions
                  </h3>
                  <div
                    class="pr-2 space-y-2 overflow-y-auto max-h-32 sm:max-h-48"
                  >
                    <div
                      v-for="permission in allPermissions.filter((p) =>
                        userPermissions.includes(p.id)
                      )"
                      :key="permission.id"
                      class="flex items-center px-2 py-2 text-xs font-medium text-green-800 border border-green-200 rounded-lg sm:px-3 sm:text-sm bg-green-50"
                    >
                      <i
                        class="mr-2 text-green-600 fa-solid fa-check-circle"
                      ></i>
                      {{ permission.title }}
                    </div>
                  </div>
                </div>

                <!-- Role Permissions -->
                <div v-if="rolePermissions.length > 0">
                  <h3
                    class="flex items-center mb-2 text-sm font-medium text-gray-900 sm:mb-3"
                  >
                    <i class="mr-2 text-blue-600 fa-solid fa-users"></i>
                    From Roles
                  </h3>
                  <div
                    class="pr-2 space-y-2 overflow-y-auto max-h-32 sm:max-h-48"
                  >
                    <div
                      v-for="permission in allPermissions.filter(
                        (p) =>
                          rolePermissions.includes(p.id) &&
                          !userPermissions.includes(p.id)
                      )"
                      :key="permission.id"
                      class="flex items-center px-2 py-2 text-xs font-medium text-blue-800 border border-blue-200 rounded-lg sm:px-3 sm:text-sm bg-blue-50"
                    >
                      <i
                        class="mr-2 text-blue-600 fa-solid fa-shield-halved"
                      ></i>
                      {{ permission.title }}
                    </div>
                  </div>
                </div>

                <!-- No Permissions -->
                <div
                  v-if="
                    userPermissions.length === 0 && rolePermissions.length === 0
                  "
                  class="py-6 text-center sm:py-8"
                >
                  <div
                    class="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-gray-100 rounded-full sm:w-12 sm:h-12 sm:mb-4"
                  >
                    <i
                      class="text-lg text-gray-400 sm:text-xl fa-solid fa-key"
                    ></i>
                  </div>
                  <h3 class="mb-2 text-xs font-medium text-gray-900 sm:text-sm">
                    No permissions assigned
                  </h3>
                  <p class="text-xs leading-relaxed text-gray-500">
                    Click "Manage<span class="hidden sm:inline">
                      Permissions</span
                    >" to assign permissions to this user
                  </p>
                </div>

                <!-- Permission Summary -->
                <div
                  v-if="
                    userPermissions.length > 0 || rolePermissions.length > 0
                  "
                  class="pt-3 border-t sm:pt-4"
                >
                  <div
                    class="flex items-center justify-between text-xs text-gray-500"
                  >
                    <span>Total Permissions</span>
                    <span class="font-medium">
                      {{
                        new Set([...userPermissions, ...rolePermissions]).size
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Permissions Modal -->
        <ModalUserPermissions
          v-model:isOpen="isModalPermissions"
          title="Manage User Permissions"
          :user="currentUser"
          @permissions-updated="handlePermissionsUpdated"
        />
      </div>
    </template>
  </Container>
</template>
